import { Component, OnInit } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import Swal from 'sweetalert2';
import {
  ItauGatewayConfig,
  newItauGatewayConfig,
  toSavePayload,
} from '../../../models/itau-gateway-config';
import { TipoFormaPagamento } from '../../../models/finance/tipo-forma-pagamento';
import { FinancesService } from '../../../services/finances.service';
import { ItauBankGatewayService } from '../../../services/itau-bank-gateway.service';
import { MessageUtil } from '../../../utils/message.util';
import {
  activationBlockers,
  certificateStatus,
  CertificateStatus,
  hasCertificate,
  isValidCnpj,
  missingRequiredFields,
  parseBackendDate,
} from './itau-bank-api.util';

/**
 * Tela "Itau Bank API": boleto do Itaú emitido pelo gateway HubFloripa, uma config por carteira de cobrança.
 * Não tem campo de Client Secret, certificado nem chave: o certificado é gerado no gateway a partir do
 * Client ID e do token temporário do Itaú, e os segredos ficam só no cofre do gateway.
 */
@Component({
  selector: 'app-itau-bank-api',
  templateUrl: './itau-bank-api.component.html',
  styleUrls: ['./itau-bank-api.component.scss']
})
export class ItauBankApiComponent implements OnInit {

  configs: ItauGatewayConfig[] = [];
  form: ItauGatewayConfig | null = null;
  /** Client ID do bloco Certificado: o backend só grava pela rota do certificado. */
  clientIdInput = '';
  tokenTemporario = '';
  busy = false;

  paymentMethods: { method: TipoFormaPagamento }[] = [];
  paymentMethodsDropdown: any[] = [];
  paymentMethodsPixDropdown: any[] = [];
  accountDropdown: any[] = [];
  billingPortfolioDropdown: any[] = [];
  billingPortfolioOptions: any[] = [];

  private savedSnapshot = '';

  constructor(
    private itauBankGatewayService: ItauBankGatewayService,
    private financesService: FinancesService,
    private spinner: NgxSpinnerService,
    private messageUtil: MessageUtil
  ) {}

  async ngOnInit(): Promise<void> {
    await this.configurePaymentMethods();
    this.getBillingPortfolios();
    this.loadConfigs();
  }

  // ---------- carregamento ----------

  loadConfigs(selectId?: number, fallbackPortfolioId?: number): void {
    this.itauBankGatewayService.listConfigs().subscribe({
      next: (configs) => {
        this.configs = configs;
        this.refreshPortfolioOptions();
        const selected = configs.find((c) => selectId != null && c.id === selectId)
          || configs.find((c) => fallbackPortfolioId != null && c.billingPortfolioId === fallbackPortfolioId);
        if (selected) {
          this.edit(selected);
        }
      },
      error: (err) => this.showError(err, 'Erro ao consultar as configurações do Itaú Bank API')
    });
  }

  private getBillingPortfolios(): void {
    this.financesService.getAllBilingPortifolio().subscribe({
      next: (data) => {
        this.billingPortfolioDropdown = (data || []).map((wallet) => ({
          label: wallet.nome,
          value: wallet.idCarteiraCobranca,
        }));
        this.refreshPortfolioOptions();
      },
      error: () => {
        this.messageUtil.generateMessage('warning', 'Atenção', 'Erro ao carregar carteiras de cobrança');
      }
    });
  }

  private getPaymentMethods(): Promise<TipoFormaPagamento[]> {
    return new Promise<TipoFormaPagamento[]>((res) => {
      this.financesService.findTipoFormasPagamento().subscribe({
        next: (data) => res(data || []),
        error: (err) => {
          this.showError(err, 'Erro ao carregar formas de pagamento');
          res([]);
        }
      });
    });
  }

  private async configurePaymentMethods(): Promise<void> {
    const data = await this.getPaymentMethods();
    const withAccounts = data.filter((method) => method.contas && method.contas.length !== 0);
    this.paymentMethods = withAccounts.map((method) => ({ method }));
    // Boleto: mesmo filtro da tela do plugin Itaú (tipo 1)
    this.paymentMethodsDropdown = withAccounts
      .filter((method) => method.tipo && method.tipo.id == 1)
      .map((method) => ({ label: method.descricao, value: method.idTipoFormaPagamento }));
    this.paymentMethodsPixDropdown = withAccounts
      .map((method) => ({ label: method.descricao, value: method.idTipoFormaPagamento }));
  }

  selectedPaymentMethods(value: number): void {
    const selected = this.paymentMethods.find((m) => m.method.idTipoFormaPagamento === value);
    this.accountDropdown = selected
      ? selected.method.contas.map((account) => ({ label: account.nome, value: account.idConta }))
      : [];
    if (this.form && this.form.account != null && !this.accountDropdown.some((a) => a.value === this.form.account)) {
      this.form.account = null;
    }
  }

  // ---------- edição ----------

  newConfig(): void {
    this.form = newItauGatewayConfig();
    this.clientIdInput = '';
    this.tokenTemporario = '';
    this.accountDropdown = [];
    this.savedSnapshot = '';
    this.refreshPortfolioOptions();
  }

  /** Abre uma config da lista (ação de quem usa a tela): começa com o bloco Certificado limpo. */
  openConfig(config: ItauGatewayConfig): void {
    this.clientIdInput = '';
    this.tokenTemporario = '';
    this.edit(config);
  }

  /** Carrega a config no formulário; mantém o que foi digitado no bloco Certificado. */
  edit(config: ItauGatewayConfig): void {
    this.form = { ...config };
    if (config.itauClientId) {
      this.clientIdInput = config.itauClientId;
    }
    this.selectedPaymentMethods(this.form.paymentMethod);
    this.savedSnapshot = JSON.stringify(toSavePayload(this.form));
    this.refreshPortfolioOptions();
  }

  cancelEdit(): void {
    this.form = null;
    this.clientIdInput = '';
    this.tokenTemporario = '';
    this.refreshPortfolioOptions();
  }

  get isNew(): boolean {
    return !this.form || this.form.id == null;
  }

  /** Há alteração não salva no formulário. */
  get isDirty(): boolean {
    return !!this.form && (this.isNew || JSON.stringify(toSavePayload(this.form)) !== this.savedSnapshot);
  }

  /** Carteiras que ainda não têm config (a do formulário sempre aparece). */
  private refreshPortfolioOptions(): void {
    const used = this.configs
      .filter((c) => !this.form || c.id !== this.form.id)
      .map((c) => c.billingPortfolioId);
    this.billingPortfolioOptions = this.billingPortfolioDropdown.filter((p) => !used.includes(p.value));
  }

  get activationBlockers(): string[] {
    return activationBlockers(this.form, this.isDirty);
  }

  get canTest(): boolean {
    return !this.busy && !!this.form && this.form.id != null && !this.isDirty;
  }

  get canActivate(): boolean {
    return !this.busy && this.activationBlockers.length === 0;
  }

  get canDeactivate(): boolean {
    return !this.busy && !!this.form && this.form.id != null && this.form.active === true;
  }

  get canGenerateCertificate(): boolean {
    return !this.busy && !!this.form && this.form.id != null
      && !!(this.clientIdInput || '').trim() && !!(this.tokenTemporario || '').trim();
  }

  certStatus(config: ItauGatewayConfig): CertificateStatus {
    return certificateStatus(config);
  }

  toDate(value: any): Date | null {
    return parseBackendDate(value);
  }

  portfolioLabel(config: ItauGatewayConfig): string {
    const option = this.billingPortfolioDropdown.find((p) => p.value === config.billingPortfolioId);
    return config.billingPortfolioName || (option ? option.label : '') || String(config.billingPortfolioId || '');
  }

  // ---------- ações ----------

  save(): void {
    const form = this.form;
    if (!form || this.busy) {
      return;
    }
    const missing = missingRequiredFields(form);
    if (missing.length > 0) {
      this.messageUtil.generateMessage('warning', 'Atenção', 'Preencha: ' + missing.join(', '));
      return;
    }
    if (!isValidCnpj(form.beneficiaryDocument)) {
      this.messageUtil.generateMessage('warning', 'Atenção', 'CNPJ do cedente inválido');
      return;
    }
    form.billingPortfolioName = this.labelOf(this.billingPortfolioDropdown, form.billingPortfolioId);
    form.paymentMethodName = this.labelOf(this.paymentMethodsDropdown, form.paymentMethod);
    form.paymentMethodPixName = this.labelOf(this.paymentMethodsPixDropdown, form.paymentMethodPix);
    form.accountName = this.labelOf(this.accountDropdown, form.account);

    this.run(this.itauBankGatewayService.saveConfig(form), 'Erro ao salvar configuração', (saved) => {
      this.messageUtil.generateMessage('success', 'Sucesso', 'Configuração salva com sucesso');
      this.afterChange(saved, form.billingPortfolioId);
    });
  }

  testConnection(): void {
    if (!this.canTest) {
      return;
    }
    const id = this.form.id;
    this.run(this.itauBankGatewayService.testConnection(id), 'Erro ao testar a conexão', (updated) => {
      const detail = updated && updated.lastTestDetail ? updated.lastTestDetail : '';
      if (!updated) {
        this.messageUtil.generateMessage('info', 'Teste de conexão', 'Teste concluído. Confira o resultado em "Último teste".');
      } else if (updated.lastTestOk === true) {
        this.messageUtil.generateMessage('success', 'Teste de conexão', detail || 'Conexão testada com sucesso');
      } else {
        this.messageUtil.generateMessage('warning', 'Teste de conexão', 'Falhou' + (detail ? ': ' + detail : ''));
      }
      this.afterChange(updated, null, id);
    });
  }

  async activate(): Promise<void> {
    if (!this.canActivate) {
      return;
    }
    const id = this.form.id;
    const confirmed = await this.confirm(
      'Ativar Itaú Bank API',
      'Os boletos em lote desta carteira passam a ser emitidos pelo gateway, e não mais pelo plugin. Confirma?'
    );
    if (!confirmed) {
      return;
    }
    this.run(this.itauBankGatewayService.activate(id), 'Erro ao ativar', (updated) => {
      this.messageUtil.generateMessage('success', 'Sucesso', 'Itaú Bank API ativado para esta carteira');
      this.afterChange(updated, null, id);
    });
  }

  async deactivate(): Promise<void> {
    if (!this.canDeactivate) {
      return;
    }
    const id = this.form.id;
    const confirmed = await this.confirm(
      'Desativar Itaú Bank API',
      'Os próximos boletos em lote desta carteira voltam a sair pelo plugin. Confirma?'
    );
    if (!confirmed) {
      return;
    }
    this.run(this.itauBankGatewayService.deactivate(id), 'Erro ao desativar', (updated) => {
      this.messageUtil.generateMessage('success', 'Sucesso', 'Itaú Bank API desativado para esta carteira');
      this.afterChange(updated, null, id);
    });
  }

  async generateCertificate(): Promise<void> {
    if (!this.canGenerateCertificate) {
      return;
    }
    const id = this.form.id;
    const replace = hasCertificate(this.form);
    if (replace) {
      const confirmed = await this.confirm(
        'Substituir certificado',
        'Já existe um certificado para esta configuração. Gerar um novo certificado invalida o secret anterior '
        + 'deste Client ID. Confirma?'
      );
      if (!confirmed) {
        return;
      }
    }
    const request = {
      clientId: this.clientIdInput,
      tokenTemporario: this.tokenTemporario,
      substituir: replace,
    };
    this.run(this.itauBankGatewayService.generateCertificate(id, request), 'Erro ao gerar o certificado no gateway',
      (updated) => {
        this.tokenTemporario = '';
        this.messageUtil.generateMessage('success', 'Sucesso', 'Certificado gerado no gateway');
        this.afterChange(updated, null, id);
      });
  }

  // ---------- apoio ----------

  private run<T>(request: Observable<T>, errorMessage: string, onSuccess: (value: T) => void): void {
    this.busy = true;
    this.spinner.show();
    request.pipe(finalize(() => {
      this.busy = false;
      this.spinner.hide();
    })).subscribe({
      next: (value) => onSuccess(value),
      error: (err) => this.showError(err, errorMessage)
    });
  }

  /** Atualiza a tela com o que o backend devolveu e recarrega a lista. */
  private afterChange(updated: ItauGatewayConfig | null, fallbackPortfolioId?: number, fallbackId?: number): void {
    if (updated) {
      this.edit(updated);
    }
    const id = updated && updated.id != null ? updated.id : fallbackId;
    this.loadConfigs(id, fallbackPortfolioId);
  }

  private labelOf(options: any[], value: any): string | null {
    const option = (options || []).find((o) => o.value === value);
    return option ? option.label : null;
  }

  private confirm(title: string, text: string): Promise<boolean> {
    return Swal.fire({
      title,
      text,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    }).then((result) => !!result.value);
  }

  /**
   * Mensagem do envelope (data.exception) no toast. No /secure do banking as recusas de regra e do
   * gateway vêm como 404 e o gateway indisponível/credencial recusada como 503, sempre com a mensagem.
   */
  private showError(err: any, fallback: string): void {
    if (err && err.status === 0) {
      return; // o interceptor já avisou da falha de comunicação
    }
    const exception = err && err.error && err.error.data ? err.error.data.exception : null;
    if (exception && exception.message) {
      this.messageUtil.generateMessage(exception.type || 'warning', exception.title || 'Atenção', exception.message);
    } else {
      this.messageUtil.generateMessage('warning', 'Atenção', fallback);
    }
  }
}
